/**
 * 「正在分享中」进度浮层（★ 2026-10-03 新增，响应用户反馈）
 *
 * ── 为什么需要它 ──────────────────────────────────────────────────────────
 * 「分享到论坛」的两条链路（预填 `publishPostDraft` / 直发 `publishDirectPost`）在
 * 用户点「确认发布」后会**先把所有弹窗关掉**，然后才去做三件真正耗时的事：
 *   ① 海报出图（canvas 绘制 + 字体等待）
 *   ② 海报上传（`POST /api/fof/upload`，还要过配图送审）
 *   ③ 建帖 / 打开发帖框（`POST /api/discussions` 或加载编辑器 chunk）
 * 弹窗一关、页面归位，**这十几秒里界面没有任何变化**，用户既不知道还在处理、
 * 也不知道成功没有（用户原话：「分享弹窗消失后，页面没有任何变化。用户都不知道发生什么了？」）。
 * 因此这里提供一个**阶段化的全局进度浮层**：转圈 + 当前阶段文案 + 收尾提示。
 *
 * ── 不重复造轮子（§9.4 复用清单）──────────────────────────────────────────
 *   · 转圈 = **核心 `LoadingIndicator`**（`flarum/common/components/LoadingIndicator`），
 *     本文件**不自写 `@keyframes spin`**，颜色/尺寸靠核心的 `--size` / `--thickness` /
 *     `--loading-indicator-color` 变量在 less 里覆盖 ⇒ 自动跟随全站与深色模式；
 *   · 遮罩 = 核心变量 `--overlay-bg`，同样零自定义色值；
 *   · 「挂 `document.body` 而不是塞进 Modal」沿用本项目既有先例
 *     （`packages/share/.../PosterPreview.tsx` 的 `jiedh-SharePosterZoom`、
 *      `packages/upload/.../imageZoom.ts` 的 `FoFUpload--ImageZoom`）——
 *     核心 `.Modal` 带 `transform: scale(.9)`，会成为 `position: fixed` 后代的包含块，
 *     所以全屏浮层**必须**直挂 body（详见《戒打卡分享重设计方案.md》与踩坑清单 P45 的 Modal 时序）。
 *
 * ── 使用契约（调用方务必遵守）────────────────────────────────────────────
 *   showPublishProgress(key)   // 进入流程时调用（幂等：已存在则只更新文案）
 *   setPublishProgress(key)    // 阶段切换时调用（同 key 直接 return，避免多余重绘）
 *   hidePublishProgress()      // **必须放在 finally**，保证成功/失败/异常都能收起
 *
 * ★ 层级口径：z-index 取 `calc(var(--zindex-alerts, 1060) - 5)`（= 1055）——
 *   高于核心 Modal（`@zindex-modal: 1050`）以遮住整页，
 *   但**低于 `app.alerts`**（1060）：海报失败时那条 warning 提示必须还能看见。
 *   数值出处：`vendor/flarum/core/less/common/variables.less` L124-125。
 */
import app from 'flarum/forum/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';

/** 浮层根节点 class（结构与视觉见 `less/forum.less` 的「分享进度浮层」一节）。 */
const OVERLAY_CLASS = 'jiedh-Checkin-publish';

/**
 * 模块级单例：同一时刻**只允许一个**进度浮层。
 * 之所以放模块级而不是组件 state：本浮层跨越「分享面板 → 确认弹窗 → 建帖 → 跳转」
 * 多个组件与多次重绘，且中途会 `m.route.set()`，任何单组件实例都活不过整条链路。
 */
let overlayHost = null;

/** 当前已渲染的文案键（用于跳过重复渲染）。 */
let currentKey = '';

/** 统一取本扩展文案：所有可见文案走 locale，禁止硬编码中文（§18.2 i18n 约定）。 */
function text(key) {
  return extractText(app.translator.trans(`jiedh-checkin.forum.${key}`));
}

/**
 * 渲染 / 更新浮层内容。
 *
 * `role="status"` + `aria-live="polite"` 打在**卡片本体**上：文案随阶段变化，
 * 读屏软件会自动播报新阶段；转圈本身则 `aria-hidden`（由核心组件自己标注）。
 */
function paint(key) {
  if (!overlayHost) return;
  currentKey = key;
  m.render(overlayHost, m('div', {
    class: `${OVERLAY_CLASS}-card`,
    role: 'status',
    'aria-live': 'polite'
  }, [
  // 核心转圈组件：`display: 'block'` 会带 100px 容器高度，已在 less 里用
  // `-spin` 覆盖为 auto；尺寸/颜色同样通过容器上的 CSS 变量覆盖。
  m(LoadingIndicator, {
    display: 'block',
    size: 'large',
    containerClassName: `${OVERLAY_CLASS}-spin`
  }), m('div', {
    class: `${OVERLAY_CLASS}-text`
  }, text(key)), m('div', {
    class: `${OVERLAY_CLASS}-hint`
  }, text('share_progress_hint'))]));
}

/**
 * 显示进度浮层（幂等）。
 *
 * @param key 文案键后缀（`jiedh-checkin.forum.<key>`），默认「正在准备分享…」
 */
export function showPublishProgress(key) {
  if (key === void 0) {
    key = 'share_progress_prepare';
  }
  if (overlayHost) {
    setPublishProgress(key);
    return;
  }
  const host = document.createElement('div');
  host.className = OVERLAY_CLASS;
  document.body.appendChild(host);
  overlayHost = host;
  paint(key);
}

/**
 * 切换阶段文案（同 key 时不做任何事，避免多余重绘与闪烁）。
 */
export function setPublishProgress(key) {
  if (!overlayHost || key === currentKey) return;
  paint(key);
}

/**
 * 收起并移除浮层（幂等）。
 *
 * ★ 调用方必须把它放在 `finally` 里：浮层是**阻断式**的（盖住整页），
 *   一旦某条异常路径漏掉 hide，页面就会被永久糊住 —— 那比没有提示更糟。
 */
export function hidePublishProgress() {
  if (!overlayHost) return;
  const host = overlayHost;
  overlayHost = null;
  currentKey = '';

  // 先让 mithril 卸载 vnode（释放其内部状态），再把节点从 body 摘掉
  m.render(host, []);
  host.remove();
}
flarum.reg.add('jiedh-checkin', 'forum/components/PublishProgress', { showPublishProgress: showPublishProgress,setPublishProgress: setPublishProgress,hidePublishProgress: hidePublishProgress, });